import { Injectable, computed, signal, DestroyRef, inject } from '@angular/core';

const DISMISS_KEY = 'pwa-install-dismissed';

/**
 * Only Chromium fires `beforeinstallprompt`. Elsewhere the user installs from the
 * browser's own menu, or (Firefox desktop) the browser can't install PWAs at all.
 */
type InstallPlatform = 'prompt' | 'firefox-android' | 'ios' | 'unsupported';

const MANUAL_INSTALL_HINTS: Partial<Record<InstallPlatform, string>> = {
  'firefox-android': 'To install, open the browser menu (⋮) and tap “Add app to Home screen”.',
  ios: 'To install, tap the Share button and choose “Add to Home Screen”.',
};

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private destroyRef = inject(DestroyRef);

  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  readonly canInstall = signal(false);
  readonly isStandalone = signal(this.detectStandalone());
  readonly isDismissed = signal(this.readDismissed());

  readonly platform = signal<InstallPlatform>(this.detectPlatform());
  readonly manualInstallHint = computed(() => MANUAL_INSTALL_HINTS[this.platform()] ?? null);

  readonly shouldShowBanner = computed(
    () => !this.isStandalone() && !this.isDismissed() && this.platform() !== 'unsupported',
  );

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem(DISMISS_KEY);

    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.canInstall.set(!this.isStandalone() && !this.isDismissed());
    };

    const onAppInstalled = () => {
      this.deferredPrompt = null;
      this.canInstall.set(false);
      this.isStandalone.set(true);
    };

    const onDisplayModeChange = () => {
      this.isStandalone.set(this.detectStandalone());
      if (this.isStandalone()) {
        this.canInstall.set(false);
      }
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    window.matchMedia('(display-mode: standalone)').addEventListener('change', onDisplayModeChange);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
      window
        .matchMedia('(display-mode: standalone)')
        .removeEventListener('change', onDisplayModeChange);
    });
  }

  async install(): Promise<boolean> {
    const prompt = this.deferredPrompt;
    if (!prompt) {
      return false;
    }

    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    this.deferredPrompt = null;
    this.canInstall.set(false);

    return outcome === 'accepted';
  }

  dismiss(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(DISMISS_KEY, 'true');
    }
    localStorage.removeItem(DISMISS_KEY);
    this.isDismissed.set(true);
    this.canInstall.set(false);
  }

  private detectStandalone(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  }

  private detectPlatform(): InstallPlatform {
    if (typeof window === 'undefined') {
      return 'unsupported';
    }

    const ua = window.navigator.userAgent;
    const isIos =
      /iPhone|iPad|iPod/i.test(ua) ||
      (/Macintosh/i.test(ua) && window.navigator.maxTouchPoints > 1);

    if (isIos) {
      return 'ios';
    }
    if (/Firefox/i.test(ua)) {
      return /Android/i.test(ua) ? 'firefox-android' : 'unsupported';
    }
    return 'prompt';
  }

  private readDismissed(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }

    return sessionStorage.getItem(DISMISS_KEY) === 'true';
  }
}
